import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getEstimateReport } from "../../api/quotations/estimateItems.ts";
import dayjs from "dayjs";

interface EstimateReportViewerProps {
  id: number;
}

interface EstimateItemProcessReportDto {
  id: number;
  processId: number;
  processName: string;
  configValues: any;
  createdAt: string;
  isActive: boolean;
}

interface EstimateItemReportDto {
  id: number;
  name: string;
  quantity: number;
  productCategoryId: number;
  productCategoryName: string;
  productId: number | null;
  description: string | null;
  attributes: any;
  productCost: number;
  createdAt: string;
  isActive: boolean;
  processes: EstimateItemProcessReportDto[];
}

interface EstimateReportDto {
  id: number;
  code: string;
  name: string;
  description: string | null;
  createdAt: string;
  isActive: boolean;
  items: EstimateItemReportDto[];
}

const EstimateReportViewer: React.FC<EstimateReportViewerProps> = ({ props }) => {
  const estimateId = props.id;
  console.log("estimateId", estimateId);
  const [report, setReport] = useState<EstimateReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await getEstimateReport(estimateId);
      setReport(response.data);
      setError(null);
    } catch (err) {
      setError("Error al cargar el reporte de la cotización");
      console.error("Error fetching estimate report:", err);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {


    if (estimateId) {
      fetchReport();
    }
  }, [estimateId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box p={2}>
        <Alert severity="warning">No se encontró el reporte de la cotización</Alert>
      </Box>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("YYYY-MM-DD HH:mm");
  };

  return (
    <Box p={2}>
      {/* Header Information */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Reporte de Cotización
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Chip label={`Código: ${report.code}`} color="primary" />
          <Chip label={`Creado: ${formatDate(report.createdAt)}`} variant="outlined" />
        </Box>
        {report.description && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {report.description}
          </Typography>
        )}
      </Box>

      {/* Items Section */}
      <Typography variant="h6" gutterBottom>
        Items de la Cotización ({report.items.length})
      </Typography>

      {report.items.map((item, index) => (
        <Accordion key={item.id} defaultExpanded={index === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">
                {item.name}
              </Typography>
              <Box display="flex" gap={1}>
                <Chip label={`Qty: ${item.quantity}`} size="small" />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {/* Item Details */}
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalles del Item:
                </Typography>
                <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Categoría:</Typography>
                    <Typography variant="body1">{item.productCategoryName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Producto ID:</Typography>
                    <Typography variant="body1">{item.productId || "N/A"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Costo:</Typography>
                    <Typography variant="body1">{formatCurrency(item.productCost)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Creado:</Typography>
                    <Typography variant="body1">{formatDate(item.createdAt)}</Typography>
                  </Box>
                </Box>
                {item.description && (
                  <Box mt={1}>
                    <Typography variant="body2" color="text.secondary">Descripción:</Typography>
                    <Typography variant="body1">{item.description}</Typography>
                  </Box>
                )}
              </Box>

              {/* Attributes */}
              {item.attributes && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Atributos:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(item.attributes, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              {/* Processes */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Procesos ({item.processes.length}):
                </Typography>
                {item.processes.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Proceso</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Creado</TableCell>
                          <TableCell>Configuración</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {item.processes.map((process) => (
                          <TableRow key={process.id}>
                            <TableCell>{process.id}</TableCell>
                            <TableCell>{process.processName}</TableCell>
                            <TableCell>
                              <Chip 
                                label={process.isActive ? "Activo" : "Inactivo"} 
                                size="small" 
                                color={process.isActive ? "success" : "error"} 
                              />
                            </TableCell>
                            <TableCell>{formatDate(process.createdAt)}</TableCell>
                            <TableCell>
                              {process.configValues ? (
                                <Box maxWidth={200} maxHeight={100} overflow="auto">
                                  <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                                    {JSON.stringify(process.configValues, null, 1)}
                                  </pre>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Sin configuración
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay procesos configurados para este item.
                  </Typography>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {report.items.length === 0 && (
        <Alert severity="info">
          No hay items configurados en esta cotización.
        </Alert>
      )}
    </Box>
  );
};

export default EstimateReportViewer; 